/**
 * Shared utilities for D3.js chart components and table/kanban renderers.
 *
 * Includes: colour palette, avatar utilities, status/priority colour maps,
 * tooltip helpers, number/date formatters, responsive resize observer hook,
 * and SVG/PNG export functions.
 */

import { useEffect, useRef, useState, useCallback } from "react";
import type { TableChartConfig, KanbanChartConfig } from "../../types/index.js";

// ---------------------------------------------------------------------------
// Avatar utilities
// ---------------------------------------------------------------------------

/** Colour palette for person avatar circles. */
export const AVATAR_COLOURS = [
	"#1B6BC4",
	"#7C3AED",
	"#0D9488",
	"#DC3545",
	"#F7A020",
	"#EC4899",
	"#5DB329",
	"#64748B",
];

/** Deterministic hash of a name string into one of the avatar colour palette entries. */
export function avatarColour(name: string): string {
	let hash = 0;
	for (let i = 0; i < name.length; i++) {
		hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
	}
	return AVATAR_COLOURS[hash % AVATAR_COLOURS.length];
}

/** Extract up to two initials from a full name string. */
export function initials(name: string): string {
	const parts = name.trim().split(/\s+/).filter(Boolean);
	if (parts.length === 0) return "?";
	if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
	return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// ---------------------------------------------------------------------------
// Status and priority colour maps
// ---------------------------------------------------------------------------

export interface StatusColour {
	dot: string;
	bg: string;
	text: string;
}

export const STATUS_COLOURS: Record<string, StatusColour> = {
	green:  { dot: "#22C55E", bg: "#F0FDF4", text: "#166534" },
	amber:  { dot: "#F59E0B", bg: "#FFFBEB", text: "#92400E" },
	red:    { dot: "#EF4444", bg: "#FEF2F2", text: "#991B1B" },
	blue:   { dot: "#3B82F6", bg: "#EFF6FF", text: "#1E40AF" },
	grey:   { dot: "#9CA3AF", bg: "#F3F4F6", text: "#374151" },
	purple: { dot: "#8B5CF6", bg: "#F5F3FF", text: "#5B21B6" },
	teal:   { dot: "#14B8A6", bg: "#F0FDFA", text: "#115E59" },
};

export const PRIORITY_COLOURS: Record<string, string> = {
	"High":   "red",
	"Medium": "amber",
	"Low":    "blue",
	"None":   "grey",
};

/** Resolve a status value to its colour key via statusMappings, defaulting to "grey". */
export function resolveStatusColour(
	value: string,
	mappings: { value: string; colour: string }[] | undefined,
): string {
	if (!mappings) return "grey";
	const match = mappings.find(
		(m) => m.value.toLowerCase() === value.toLowerCase(),
	);
	return match?.colour ?? "grey";
}

// ---------------------------------------------------------------------------
// Chart colour palette
// ---------------------------------------------------------------------------

/** Caldere-aligned chart colour palette. */
export const CHART_COLOURS = {
	primary: "#1B6BC4",
	secondary: "#F7A020",
	success: "#5DB329",
	danger: "#DC3545",
	purple: "#7C3AED",
	teal: "#0D9488",
	slate: "#64748B",
	pink: "#EC4899",
} as const;

/** Ordered series colour sequence for multi-series charts. */
export const SERIES_COLOURS: string[] = [
	CHART_COLOURS.primary,
	CHART_COLOURS.secondary,
	CHART_COLOURS.success,
	CHART_COLOURS.purple,
	CHART_COLOURS.teal,
	CHART_COLOURS.danger,
	CHART_COLOURS.pink,
	CHART_COLOURS.slate,
];

// ---------------------------------------------------------------------------
// Tooltip helper
// ---------------------------------------------------------------------------

/**
 * Positions a tooltip element near the mouse cursor.
 * Clamps to viewport edges to prevent overflow.
 */
export function positionTooltip(
	tooltipEl: HTMLElement,
	event: MouseEvent | { clientX: number; clientY: number }
): void {
	const margin = 12;
	const vpW = window.innerWidth;
	const vpH = window.innerHeight;
	const rect = tooltipEl.getBoundingClientRect();
	const ttW = rect.width || 160;
	const ttH = rect.height || 40;

	let x = event.clientX + margin;
	let y = event.clientY - ttH / 2;

	// Clamp right edge
	if (x + ttW > vpW - margin) {
		x = event.clientX - ttW - margin;
	}
	// Clamp bottom edge
	if (y + ttH > vpH - margin) {
		y = vpH - ttH - margin;
	}
	// Clamp top edge
	if (y < margin) {
		y = margin;
	}

	tooltipEl.style.left = `${x}px`;
	tooltipEl.style.top = `${y}px`;
}

// ---------------------------------------------------------------------------
// Number and date formatters
// ---------------------------------------------------------------------------

/** Formats a number with commas for thousands separators. */
export function formatNumber(value: number): string {
	return new Intl.NumberFormat("en-GB").format(value);
}

/** Formats an ISO date string as DD MMM YYYY (e.g. "15 Jan 2025"). */
export function formatDate(isoDate: string): string {
	const d = new Date(isoDate);
	if (isNaN(d.getTime())) return isoDate;
	return new Intl.DateTimeFormat("en-GB", {
		day: "2-digit",
		month: "short",
		year: "numeric",
	}).format(d);
}

/** Formats an ISO date string as DD MMM (e.g. "15 Jan") for axis labels. */
export function formatDateShort(isoDate: string | Date): string {
	const d = typeof isoDate === "string" ? new Date(isoDate) : isoDate;
	if (isNaN(d.getTime())) return String(isoDate);
	return new Intl.DateTimeFormat("en-GB", {
		day: "2-digit",
		month: "short",
	}).format(d);
}

// ---------------------------------------------------------------------------
// Responsive resize observer hook
// ---------------------------------------------------------------------------

/** Dimensions returned by useResizeObserver. */
export interface ContainerSize {
	width: number;
	height: number;
}

/**
 * Watches a container element and returns its current { width, height }.
 * Re-renders the component whenever either dimension changes.
 *
 * Returns zeroes until the element is mounted and measured — callers should
 * guard against zero values before rendering D3 charts.
 */
export function useResizeObserver(ref: React.RefObject<Element | null>): ContainerSize {
	const [size, setSize] = useState<ContainerSize>({ width: 0, height: 0 });

	const updateSize = useCallback(() => {
		if (ref.current) {
			const { clientWidth, clientHeight } = ref.current;
			setSize((prev) => {
				// Avoid redundant re-renders when dimensions haven't changed.
				if (prev.width === clientWidth && prev.height === clientHeight) return prev;
				return { width: clientWidth, height: clientHeight };
			});
		}
	}, [ref]);

	useEffect(() => {
		// Measure immediately — catches the common inline case where the element
		// is already laid out on mount.
		updateSize();

		// Defer a second measurement to the next animation frame. This handles
		// portal-mounted charts (e.g. ChartModal) where the host element's
		// dimensions have not yet resolved at synchronous effect time because
		// the portal is mid-animation when the component first mounts.
		const rafId = requestAnimationFrame(() => {
			updateSize();
		});

		if (!ref.current) return () => cancelAnimationFrame(rafId);

		const observer = new ResizeObserver(() => {
			updateSize();
		});

		observer.observe(ref.current);

		return () => {
			cancelAnimationFrame(rafId);
			observer.disconnect();
		};
	}, [ref, updateSize]);

	return size;
}

// ---------------------------------------------------------------------------
// Export utilities
// ---------------------------------------------------------------------------

/** Creates a temporary anchor element, clicks it, then revokes the blob URL. */
function triggerDownload(url: string, filename: string): void {
	const a = document.createElement("a");
	a.href = url;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	setTimeout(() => URL.revokeObjectURL(url), 10000);
}

/**
 * Inlines computed styles into SVG clone elements so the exported image
 * matches the on-screen rendering faithfully.
 */
function inlineStyles(sourceEl: Element, targetEl: Element): void {
	const computed = window.getComputedStyle(sourceEl);
	const target = targetEl as HTMLElement;

	// Only copy relevant SVG-affecting properties to keep output clean
	const relevantProps = [
		"font-family",
		"font-size",
		"font-weight",
		"fill",
		"stroke",
		"stroke-width",
		"stroke-dasharray",
		"opacity",
	];

	for (const prop of relevantProps) {
		const val = computed.getPropertyValue(prop);
		if (val) {
			target.style.setProperty(prop, val);
		}
	}

	// Recurse into children
	const children = sourceEl.children;
	const targetChildren = targetEl.children;
	for (let i = 0; i < children.length; i++) {
		if (targetChildren[i]) {
			inlineStyles(children[i], targetChildren[i]);
		}
	}
}

/**
 * Exports an SVG element as a PNG file.
 * Clones the SVG, inlines computed styles, renders to a canvas, then downloads.
 */
export async function exportSvgAsPng(
	svgEl: SVGSVGElement,
	filename: string = "chart.png",
	scale: number = 2
): Promise<void> {
	const clone = svgEl.cloneNode(true) as SVGSVGElement;
	const svgNs = "http://www.w3.org/2000/svg";

	// Set background so PNG isn't transparent
	const bg = document.createElementNS(svgNs, "rect");
	bg.setAttribute("width", "100%");
	bg.setAttribute("height", "100%");
	bg.setAttribute("fill", "#ffffff");
	clone.insertBefore(bg, clone.firstChild);

	// Inline styles into clone
	inlineStyles(svgEl, clone);

	const svgData = new XMLSerializer().serializeToString(clone);
	const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
	const svgUrl = URL.createObjectURL(svgBlob);

	const img = new Image();
	img.onload = () => {
		const bbox = svgEl.getBoundingClientRect();
		const canvas = document.createElement("canvas");
		canvas.width = bbox.width * scale;
		canvas.height = bbox.height * scale;
		const ctx = canvas.getContext("2d")!;
		ctx.scale(scale, scale);
		ctx.drawImage(img, 0, 0);
		URL.revokeObjectURL(svgUrl);

		canvas.toBlob((blob) => {
			if (!blob) return;
			const pngUrl = URL.createObjectURL(blob);
			triggerDownload(pngUrl, filename);
		}, "image/png");
	};
	img.src = svgUrl;
}

/**
 * Exports an SVG element as an SVG file.
 */
export function exportSvgAsSvg(
	svgEl: SVGSVGElement,
	filename: string = "chart.svg"
): void {
	const clone = svgEl.cloneNode(true) as SVGSVGElement;
	inlineStyles(svgEl, clone);

	const svgData = new XMLSerializer().serializeToString(clone);
	const blob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
	const url = URL.createObjectURL(blob);
	triggerDownload(url, filename);
}

// ---------------------------------------------------------------------------
// Ref to SVG helper (for export from container ref)
// ---------------------------------------------------------------------------

/**
 * Finds the first SVGSVGElement within a container element.
 */
export function findSvgInContainer(container: HTMLElement): SVGSVGElement | null {
	return container.querySelector("svg");
}

// ---------------------------------------------------------------------------
// Table copy utility
// ---------------------------------------------------------------------------

/**
 * Copies a Kanban board's data to the clipboard as tab-separated text.
 *
 * Produces a header row (Column, Task, Priority, Assignees, Progress, Due Date)
 * followed by one row per card across all columns. Empty columns produce no
 * card rows. All cards are included regardless of maxCardsPerColumn — the
 * truncation is a display concern only.
 */
export function copyKanbanAsText(config: KanbanChartConfig): string {
	const header = "Column\tTask\tPriority\tAssignees\tProgress\tDue Date";
	const rows: string[] = [];

	for (const column of config.columns) {
		for (const card of column.cards) {
			const priority = card.priority ?? "";
			const assignees = card.assignees?.join(", ") ?? "";
			const progress =
				card.percentComplete !== undefined && card.percentComplete !== null
					? `${card.percentComplete}%`
					: "";
			const dueDate = card.dueDate ?? "";
			rows.push(
				`${column.title}\t${card.title}\t${priority}\t${assignees}\t${progress}\t${dueDate}`,
			);
		}
	}

	return [header, ...rows].join("\n");
}

/**
 * Copies a Kanban board's data to the clipboard. Returns true on success.
 */
export async function copyKanbanToClipboard(config: KanbanChartConfig): Promise<boolean> {
	try {
		const text = copyKanbanAsText(config);
		await navigator.clipboard.writeText(text);
		return true;
	} catch {
		return false;
	}
}

/**
 * Copies a table's data to the clipboard as tab-separated text.
 *
 * The first row contains column labels; subsequent rows contain raw cell
 * values. Missing keys are represented as empty strings. Returns true on
 * success, false if the clipboard API is unavailable or throws.
 */
export async function copyTableAsText(config: TableChartConfig): Promise<boolean> {
	try {
		const { columns, data } = config;

		// Build header row from column labels
		const headerRow = columns.map((col) => col.label).join("\t");

		// Build each data row using raw values (no formatting)
		const dataRows = (data ?? []).map((row) => {
			return columns
				.map((col) => {
					const value = row[col.key];
					// Represent null/undefined as empty string
					return value === null || value === undefined ? "" : String(value);
				})
				.join("\t");
		});

		const text = [headerRow, ...dataRows].join("\n");
		await navigator.clipboard.writeText(text);
		return true;
	} catch {
		return false;
	}
}
